import { makePagePopupSelector, screenSelector } from "@client/selectors";
import { getDefaultPagePopupSettings } from "@client/settings/pagePopup";
import { type BaseAppState } from "@client/types";
import { createSelector } from "reselect";
import { getInherittedSettingValue, getScreenSettings, mergeCompositeSetting } from "./settings";

export type PopupTriggerEvent = "exitIntent" | "pageLoad";
export type PopupTrigger = {
  event: PopupTriggerEvent;
  delay?: string;
  frequency?: string;
};

const makePagePopupSettingsSelector = () => {
  const pagePopupSelector = makePagePopupSelector();
  return createSelector([pagePopupSelector, screenSelector], (pagePopup, screen) => {
    if (!pagePopup) return getDefaultPagePopupSettings();

    return mergeCompositeSetting(
      (x) => getScreenSettings(pagePopup.settings, x),
      pagePopup.local ? (x) => getScreenSettings(pagePopup.local!, x) : null,
      screen
    );
  });
};

export const getPopupTrigger = (state: BaseAppState, id: string): PopupTrigger | null => {
  const settings = makePagePopupSettingsSelector()(state, { id });
  const screen = screenSelector(state);
  const event = getInherittedSettingValue<PopupTriggerEvent>(settings, "", "triggerEvent", screen);
  const delay = getInherittedSettingValue(settings, "", "triggerDelay", screen);
  const frequency = getInherittedSettingValue(settings, "", "triggerFrequency", screen);

  if (!event) return null;

  return {
    delay,
    event,
    frequency
  };
};

export const shouldShowPopup = (state: BaseAppState, id: string): boolean => {
  const settings = makePagePopupSettingsSelector()(state, { id });
  const screen = screenSelector(state);
  return getInherittedSettingValue(settings, "", "showOnThisPage", screen);
};
